function injectLivenessKeyframes(): void {
  if (document.getElementById("sc-liveness-kf")) return;
  const s = document.createElement("style");
  s.id = "sc-liveness-kf";
  s.textContent = `
    @keyframes sc-pop-in {
      0%   { transform:scale(0.7); opacity:0; }
      60%  { transform:scale(1.05); }
      100% { transform:scale(1); opacity:1; }
    }
    @keyframes sc-scan-dot {
      0%,100% { opacity:0.3; transform:scale(0.85); }
      50%     { opacity:1;   transform:scale(1); }
    }
    @keyframes sc-scanline-sweep {
      0%   { transform:translateY(-4%); opacity:0; }
      8%   { opacity:0.9; }
      92%  { opacity:0.9; }
      100% { transform:translateY(104%); opacity:0; }
    }
    @keyframes sc-corner-pulse {
      0%,100% { opacity:0.55; }
      50%     { opacity:1; }
    }
    @keyframes sc-oval-breathe {
      0%,100% { opacity:0.5; }
      50%     { opacity:0.85; }
    }
  `;
  document.head.appendChild(s);
}

export interface UIChallenge {
  time_limit_seconds: number;
  instruction: string;
}

/**
 * Single-view passive liveness scan UI: full-width camera, oval face guide,
 * one instruction line, a subtle scanning progress ring. No step-by-step
 * challenge list — replaces the earlier two-column "BLINK / TURN_HEAD /
 * OPEN_MOUTH" checklist design with a plain natural-motion scan.
 */
/** Minimal color contract LivenessUI needs — a subset of flow/theme.ts's
 * ThemeColors so this module doesn't have to import the flow layer. */
export interface LivenessUIColors {
  primary: string;
  success: string;
  error: string;
  warning: string;
}

const DEFAULT_COLORS: LivenessUIColors = {
  primary: "#3b82f6",
  success: "#22c55e",
  error: "#ef4444",
  warning: "#fbbf24",
};

export class LivenessUI {
  private root: HTMLDivElement | null = null;
  private colors: LivenessUIColors;
  private videoEl: HTMLVideoElement | null = null;
  private instructionEl: HTMLDivElement | null = null;
  private confidenceRingEl: SVGEllipseElement | null = null;
  private bgOvalEl: SVGEllipseElement | null = null;
  private guideWrapEl: HTMLDivElement | null = null;
  private scanlineEl: HTMLDivElement | null = null;
  private overlayEl: HTMLDivElement | null = null;
  private dotsEl: HTMLDivElement | null = null;
  private docCaptureRoot: HTMLDivElement | null = null;
  private scanStartMs = 0;
  private scanDurationMs = 0;
  private faceTracked = false;

  constructor(colors?: Partial<LivenessUIColors>) {
    this.colors = { ...DEFAULT_COLORS, ...colors };
  }

  mount(container: HTMLElement, challenge: UIChallenge): HTMLVideoElement {
    injectLivenessKeyframes();

    this.root = document.createElement("div");
    this.root.style.cssText = `
      position:relative;width:100%;max-width:420px;margin:0 auto;
      background:#0a0a0a;border-radius:16px;overflow:hidden;
      font-family:system-ui,-apple-system,sans-serif;
      box-shadow:0 8px 32px rgba(0,0,0,0.4);
      aspect-ratio:3/4;
    `;

    this.videoEl = document.createElement("video");
    this.videoEl.autoplay = true;
    this.videoEl.playsInline = true;
    this.videoEl.muted = true;
    this.videoEl.setAttribute("aria-label", "Live camera preview for face verification");
    this.videoEl.style.cssText = `
      width:100%;height:100%;display:block;
      filter:brightness(1.03) contrast(1.03);
      transform:scaleX(-1);object-fit:cover;
    `;

    // Dim everything outside the oval so the guide reads as a scan aperture
    // rather than a shape floating over the raw feed.
    const vignette = document.createElement("div");
    vignette.style.cssText = `
      position:absolute;inset:0;pointer-events:none;
      background:radial-gradient(ellipse 34% 44% at 50% 47%, transparent 55%, rgba(0,0,0,0.55) 100%);
    `;

    // Oval guide — centred
    const guideWrap = document.createElement("div");
    guideWrap.style.cssText = `
      position:absolute;top:50%;left:50%;
      transform:translate(-50%,-52%);
      width:64%;aspect-ratio:140/180;pointer-events:none;
      filter:drop-shadow(0 0 18px rgba(59,130,246,0.35));
      transition:filter 0.3s ease;
    `;
    this.guideWrapEl = guideWrap;

    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 140 180");
    svg.setAttribute("aria-hidden", "true");
    svg.style.cssText = "width:100%;height:100%;overflow:visible;";

    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    const clip = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
    clip.setAttribute("id", "sc-oval-clip");
    const clipOval = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
    clipOval.setAttribute("cx", "70");
    clipOval.setAttribute("cy", "90");
    clipOval.setAttribute("rx", "64");
    clipOval.setAttribute("ry", "86");
    clip.appendChild(clipOval);
    defs.appendChild(clip);
    svg.appendChild(defs);

    // Faint dashed inner track — gives the guide a "scanner reticle" feel
    // rather than a single plain outline.
    const trackOval = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
    trackOval.setAttribute("cx", "70");
    trackOval.setAttribute("cy", "90");
    trackOval.setAttribute("rx", "58");
    trackOval.setAttribute("ry", "80");
    trackOval.setAttribute("fill", "none");
    trackOval.setAttribute("stroke", "rgba(255,255,255,0.16)");
    trackOval.setAttribute("stroke-width", "1");
    trackOval.setAttribute("stroke-dasharray", "2 5");
    svg.appendChild(trackOval);

    const bgOval = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
    bgOval.setAttribute("cx", "70");
    bgOval.setAttribute("cy", "90");
    bgOval.setAttribute("rx", "64");
    bgOval.setAttribute("ry", "86");
    bgOval.setAttribute("fill", "none");
    bgOval.setAttribute("stroke", "rgba(255,255,255,0.4)");
    bgOval.setAttribute("stroke-width", "1.5");
    bgOval.style.cssText = "animation:sc-oval-breathe 2.4s ease-in-out infinite;transition:stroke 0.3s ease;";
    this.bgOvalEl = bgOval;

    // Progress ring — traces the SAME path as bgOval (identical cx/cy/rx/ry)
    // so it always reads as "this oval is filling in," never as a separate
    // floating stroke. A flat cap (not round) is what makes a short early-
    // scan arc look like a ring segment sitting on the track rather than a
    // stray dash — a round cap on a thin, short arc reads as a loose curve
    // instead of "part of a circle."
    const confOval = document.createElementNS("http://www.w3.org/2000/svg", "ellipse");
    confOval.setAttribute("cx", "70");
    confOval.setAttribute("cy", "90");
    confOval.setAttribute("rx", "64");
    confOval.setAttribute("ry", "86");
    confOval.setAttribute("fill", "none");
    confOval.setAttribute("stroke", this.colors.primary);
    confOval.setAttribute("stroke-width", "3");
    confOval.setAttribute("stroke-linecap", "butt");
    const circ = 2 * Math.PI * Math.sqrt((64 * 64 + 86 * 86) / 2);
    confOval.setAttribute("stroke-dasharray", `${circ}`);
    confOval.setAttribute("stroke-dashoffset", `${circ}`);
    confOval.style.cssText = "transition:stroke-dashoffset 0.2s linear,stroke 0.3s ease;transform:rotate(-90deg);transform-origin:70px 90px;filter:drop-shadow(0 0 3px rgba(0,0,0,0.25));";
    this.confidenceRingEl = confOval;

    // Corner brackets — reads as an active biometric scan aperture, matching
    // the document-capture screen's bracket language for visual consistency.
    const bracketGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
    bracketGroup.setAttribute("stroke", this.colors.primary);
    bracketGroup.setAttribute("stroke-width", "3");
    bracketGroup.setAttribute("stroke-linecap", "round");
    bracketGroup.setAttribute("fill", "none");
    bracketGroup.style.cssText = "animation:sc-corner-pulse 2s ease-in-out infinite;";
    const bracketLen = 14;
    const bx0 = 70 - 64 * 0.74, bx1 = 70 + 64 * 0.74;
    const by0 = 90 - 86 * 0.82, by1 = 90 + 86 * 0.82;
    const brackets = [
      `M${bx0},${by0 + bracketLen} L${bx0},${by0} L${bx0 + bracketLen},${by0}`,
      `M${bx1 - bracketLen},${by0} L${bx1},${by0} L${bx1},${by0 + bracketLen}`,
      `M${bx0},${by1 - bracketLen} L${bx0},${by1} L${bx0 + bracketLen},${by1}`,
      `M${bx1 - bracketLen},${by1} L${bx1},${by1} L${bx1},${by1 - bracketLen}`,
    ];
    for (const d of brackets) {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("d", d);
      bracketGroup.appendChild(path);
    }

    svg.appendChild(bgOval);
    svg.appendChild(confOval);
    svg.appendChild(bracketGroup);
    guideWrap.appendChild(svg);

    // Scan-line sweep — clipped to the oval, gives a visible "actively
    // scanning" motion cue instead of a static outline.
    const scanlineClipWrap = document.createElement("div");
    scanlineClipWrap.style.cssText = `
      position:absolute;inset:0;
      clip-path:ellipse(45.7% 47.8% at 50% 50%);
      overflow:hidden;
    `;
    const scanline = document.createElement("div");
    scanline.style.cssText = `
      position:absolute;left:0;right:0;height:26%;top:0;
      background:linear-gradient(180deg,transparent 0%,rgba(59,130,246,0.32) 45%,rgba(147,197,253,0.55) 50%,rgba(59,130,246,0.32) 55%,transparent 100%);
      animation:sc-scanline-sweep 2.6s ease-in-out infinite;
    `;
    this.scanlineEl = scanline;
    scanlineClipWrap.appendChild(scanline);
    guideWrap.appendChild(scanlineClipWrap);

    // Instruction — top, gradient scrim
    const camTop = document.createElement("div");
    camTop.style.cssText = `
      position:absolute;top:0;left:0;right:0;
      background:linear-gradient(180deg,rgba(0,0,0,0.8) 0%,transparent 100%);
      padding:14px 16px 32px;
    `;
    this.instructionEl = document.createElement("div");
    this.instructionEl.setAttribute("aria-live", "polite");
    this.instructionEl.setAttribute("role", "status");
    this.instructionEl.style.cssText = `
      color:#fff;font-size:14px;font-weight:600;text-align:center;line-height:1.4;
    `;
    this.instructionEl.textContent = challenge.instruction;
    camTop.appendChild(this.instructionEl);

    // Scan progress dots — bottom
    const camBottom = document.createElement("div");
    camBottom.style.cssText = `
      position:absolute;bottom:0;left:0;right:0;
      background:linear-gradient(0deg,rgba(0,0,0,0.7) 0%,transparent 100%);
      padding:28px 10px 18px;display:flex;justify-content:center;
    `;
    this.dotsEl = document.createElement("div");
    this.dotsEl.style.cssText = "display:flex;gap:6px;";
    for (let i = 0; i < 3; i++) {
      const dot = document.createElement("div");
      dot.style.cssText = `
        width:6px;height:6px;border-radius:50%;background:${this.colors.primary};
        animation:sc-scan-dot 1.1s ease-in-out ${i * 0.18}s infinite;
      `;
      this.dotsEl.appendChild(dot);
    }
    camBottom.appendChild(this.dotsEl);

    this.root.appendChild(this.videoEl);
    this.root.appendChild(vignette);
    this.root.appendChild(guideWrap);
    this.root.appendChild(camTop);
    this.root.appendChild(camBottom);

    // Result overlay
    this.overlayEl = document.createElement("div");
    this.overlayEl.style.cssText = `
      position:absolute;inset:0;display:none;align-items:center;
      justify-content:center;z-index:10;
      background:rgba(0,0,0,0.88);backdrop-filter:blur(8px);
    `;
    this.root.appendChild(this.overlayEl);

    container.appendChild(this.root);
    this.scanDurationMs = challenge.time_limit_seconds * 1000;
    this.startScanClock();

    return this.videoEl;
  }

  /**
   * Resets the progress ring's clock to now. The caller (liveness.ts) mounts
   * the UI, then waits ~1s for camera AE/AF to settle before the detection
   * loop actually starts — call this again right when detection begins so
   * the ring's timing matches the real scan window instead of running ahead.
   */
  startScanClock(): void {
    this.scanStartMs = performance.now();
  }

  /** Advances the scan progress ring based on elapsed time in the window. */
  updateScanProgress(livenessConfirmed: boolean): void {
    if (!this.confidenceRingEl) return;
    const elapsed = performance.now() - this.scanStartMs;
    const pct = this.scanDurationMs > 0 ? Math.min(1, elapsed / this.scanDurationMs) : 0;
    const circ = 2 * Math.PI * Math.sqrt((64 * 64 + 86 * 86) / 2);
    this.confidenceRingEl.setAttribute("stroke-dashoffset", `${circ * (1 - pct)}`);
    this.confidenceRingEl.setAttribute("stroke", livenessConfirmed ? this.colors.success : this.colors.primary);
  }

  /**
   * Reflects whether a face is currently detected in-frame — brightens the
   * oval guide's glow/brackets when locked on, and dims the scan-line sweep
   * so the UI visibly distinguishes "searching" from "actively scanning".
   */
  setFaceTracking(tracked: boolean): void {
    if (this.faceTracked === tracked) return;
    this.faceTracked = tracked;
    if (this.guideWrapEl) {
      this.guideWrapEl.style.filter = tracked
        ? "drop-shadow(0 0 22px rgba(34,197,94,0.45))"
        : "drop-shadow(0 0 18px rgba(59,130,246,0.35))";
    }
    if (this.bgOvalEl) {
      this.bgOvalEl.setAttribute("stroke", tracked ? "rgba(74,222,128,0.55)" : "rgba(255,255,255,0.4)");
    }
    if (this.scanlineEl) {
      this.scanlineEl.style.animationPlayState = tracked ? "running" : "paused";
      this.scanlineEl.style.opacity = tracked ? "1" : "0.4";
    }
  }

  updateInstruction(text: string): void {
    if (this.instructionEl) this.instructionEl.textContent = text;
  }

  showResult(status: "processing" | "failed", _verificationId?: number): Promise<void> {
    return new Promise((resolve) => {
      if (!this.overlayEl) { resolve(); return; }
      const ok = status === "processing";
      this.overlayEl.style.display = "flex";
      this.overlayEl.innerHTML = `
        <div style="text-align:center;color:#fff;animation:sc-pop-in 0.35s ease;padding:24px;">
          <div style="width:72px;height:72px;border-radius:50%;margin:0 auto 16px;display:flex;align-items:center;justify-content:center;font-size:32px;
            background:${ok ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)"};
            border:3px solid ${ok ? this.colors.success : this.colors.error};">
            ${ok ? "✓" : "✕"}
          </div>
          <div style="font-size:17px;font-weight:700;margin-bottom:8px;">${ok ? "Verification submitted" : "Submission failed"}</div>
          <div style="font-size:12px;opacity:0.6;line-height:1.6;">${ok ? "Hold on while we wrap up…" : "Something went wrong. Please try again."}</div>
        </div>`;
      setTimeout(resolve, 1800);
    });
  }

  showTimeout(onRetry: () => void): Promise<void> {
    return new Promise((resolve) => {
      if (!this.overlayEl) { resolve(); return; }
      this.overlayEl.style.display = "flex";
      this.overlayEl.innerHTML = `
        <div style="text-align:center;color:#fff;animation:sc-pop-in 0.35s ease;padding:24px;">
          <div style="width:72px;height:72px;border-radius:50%;margin:0 auto 14px;display:flex;align-items:center;justify-content:center;font-size:32px;background:rgba(251,191,36,0.15);border:3px solid ${this.colors.warning};">⏱</div>
          <div style="font-size:17px;font-weight:700;margin-bottom:8px;">Time's Up</div>
          <div style="font-size:12px;opacity:0.6;margin-bottom:18px;">We couldn't confirm liveness in time. Please try again.</div>
          <button id="sc-retry-btn" style="padding:11px 28px;border-radius:10px;background:${this.colors.primary};color:#fff;border:none;cursor:pointer;font-size:14px;font-weight:600;width:100%;max-width:180px;">Try Again</button>
        </div>`;
      const btn = this.overlayEl.querySelector("#sc-retry-btn") as HTMLButtonElement | null;
      if (btn) {
        btn.addEventListener("click", () => {
          // Prevent a double-click from firing two concurrent retries.
          if (btn.disabled) return;
          btn.disabled = true;
          btn.style.opacity = "0.6";
          btn.style.cursor = "default";
          onRetry();
          resolve();
        });
      }
      else { setTimeout(resolve, 5000); }
    });
  }

  getVideoElement(): HTMLVideoElement | null { return this.videoEl; }

  mountDocumentCapture(container: HTMLElement, stream: MediaStream): Promise<Blob> {
    return new Promise((resolve, reject) => {
      injectLivenessKeyframes();
      const docRoot = document.createElement("div");
      docRoot.style.cssText = `position:relative;width:100%;max-width:420px;margin:0 auto;background:#0a0a0a;border-radius:16px;overflow:hidden;font-family:system-ui,-apple-system,sans-serif;box-shadow:0 8px 32px rgba(0,0,0,0.4);`;
      this.docCaptureRoot = docRoot;
      const docVideo = document.createElement("video");
      docVideo.autoplay = true; docVideo.playsInline = true; docVideo.muted = true;
      docVideo.style.cssText = "width:100%;display:block;aspect-ratio:4/3;object-fit:cover;";
      docVideo.srcObject = stream;
      const guideOverlay = document.createElement("div");
      guideOverlay.style.cssText = "position:absolute;inset:0;pointer-events:none;display:flex;align-items:center;justify-content:center;";
      guideOverlay.innerHTML = `<div style="width:83%;height:53%;border:2px dashed rgba(255,255,255,0.55);border-radius:10px;box-shadow:0 0 0 2000px rgba(0,0,0,0.45);position:relative;"><div style="position:absolute;top:-2px;left:-2px;width:18px;height:18px;border-top:3px solid ${this.colors.primary};border-left:3px solid ${this.colors.primary};border-radius:4px 0 0 0;"></div><div style="position:absolute;top:-2px;right:-2px;width:18px;height:18px;border-top:3px solid ${this.colors.primary};border-right:3px solid ${this.colors.primary};border-radius:0 4px 0 0;"></div><div style="position:absolute;bottom:-2px;left:-2px;width:18px;height:18px;border-bottom:3px solid ${this.colors.primary};border-left:3px solid ${this.colors.primary};border-radius:0 0 0 4px;"></div><div style="position:absolute;bottom:-2px;right:-2px;width:18px;height:18px;border-bottom:3px solid ${this.colors.primary};border-right:3px solid ${this.colors.primary};border-radius:0 0 4px 0;"></div></div>`;
      const header = document.createElement("div");
      header.style.cssText = "position:absolute;top:0;left:0;right:0;background:linear-gradient(180deg,rgba(0,0,0,0.88) 0%,transparent 100%);padding:18px 16px 40px;text-align:center;";
      header.innerHTML = `<div style="color:#fff;font-size:16px;font-weight:700;margin-bottom:6px;">Scan Your ID Document</div><div style="color:rgba(255,255,255,0.6);font-size:12px;line-height:1.6;">Fit your document inside the frame.<br>Make sure all text and corners are visible.</div>`;
      const bottomPanel = document.createElement("div");
      bottomPanel.style.cssText = "position:absolute;bottom:0;left:0;right:0;background:linear-gradient(0deg,rgba(0,0,0,0.92) 0%,rgba(0,0,0,0.6) 70%,transparent 100%);padding:16px;";
      bottomPanel.innerHTML = `<div style="font-size:11px;color:rgba(255,255,255,0.4);text-align:center;margin-bottom:12px;">Good lighting · No glare · All 4 corners visible</div><button id="sc-capture-doc-btn" style="width:100%;padding:12px;border-radius:12px;background:${this.colors.primary};color:#fff;border:none;cursor:pointer;font-size:15px;font-weight:700;margin-bottom:8px;">Capture Document</button><label style="width:100%;display:flex;align-items:center;justify-content:center;gap:6px;padding:9px;border-radius:12px;border:1.5px solid rgba(255,255,255,0.18);cursor:pointer;color:rgba(255,255,255,0.6);font-size:13px;font-weight:500;"><span>📁</span><span>Upload from gallery instead</span><input id="sc-upload-doc-input" type="file" accept="image/*" style="display:none;" /></label>`;
      docRoot.appendChild(docVideo); docRoot.appendChild(guideOverlay); docRoot.appendChild(header); docRoot.appendChild(bottomPanel);
      container.appendChild(docRoot);
      docVideo.play().catch(() => {});
      const cleanup = () => { try { docRoot.remove(); } catch(_){} this.docCaptureRoot = null; };
      const captureBtn = bottomPanel.querySelector("#sc-capture-doc-btn") as HTMLButtonElement;
      if (captureBtn) {
        captureBtn.addEventListener("click", () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = docVideo.videoWidth || 1280; canvas.height = docVideo.videoHeight || 720;
            const ctx = canvas.getContext("2d");
            if (!ctx) { reject(new Error("Canvas context unavailable")); return; }
            ctx.drawImage(docVideo, 0, 0);
            canvas.toBlob((blob) => { if (blob) { cleanup(); resolve(blob); } else { reject(new Error("Failed to capture document image")); } }, "image/jpeg", 0.92);
          } catch (err) { reject(err); }
        });
      }
      const uploadInput = bottomPanel.querySelector("#sc-upload-doc-input") as HTMLInputElement;
      if (uploadInput) { uploadInput.addEventListener("change", () => { const file = uploadInput.files?.[0]; if (file) { cleanup(); resolve(file); } }); }
    });
  }

  unmount(): void {
    try { this.docCaptureRoot?.remove(); } catch(_){} this.docCaptureRoot = null;
    try { this.root?.remove(); } catch(_){}
    this.root = null; this.videoEl = null; this.instructionEl = null;
    this.confidenceRingEl = null; this.overlayEl = null; this.dotsEl = null;
    this.bgOvalEl = null; this.guideWrapEl = null; this.scanlineEl = null;
    this.faceTracked = false;
  }
}
